import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';

/**
 * FloatingNavButtons component for quick access to Lectures and Publications
 * Displays two floating action buttons on the right side of mobile screens
 * Only visible on the home page
 * @returns {JSX.Element} FloatingNavButtons component
 */
const FloatingNavButtons = () => {
  const { theme } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  // Create refs at the top level
  const buttonRefs = useRef({});

  // Only show on home page
  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  // Check if mobile
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  // Fade in effect after mount
  useEffect(() => {
    if (isHomePage && isMobile) {
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isHomePage, isMobile]);

  // Don't render if not on home page or not mobile
  if (!isHomePage || !isMobile) {
    return null;
  }

  const buttons = [
    {
      id: 'lectures',
      label: 'Lectures',
      icon: '📚',
      path: '/',
      color: theme.colors.primary
    },
    {
      id: 'publications',
      label: 'Publications',
      icon: '📖',
      path: '/publications',
      color: theme.colors.accent || theme.colors.secondary
    }
  ];
  
  // Initialize refs for each button
  buttons.forEach(button => {
    if (!buttonRefs.current[button.id]) {
      buttonRefs.current[button.id] = React.createRef();
    }
  });

  const buttonStyles = {
    container: {
      position: 'fixed',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      zIndex: 100,
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.5s ease-in-out'
    },
    button: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      padding: '8px',
      textDecoration: 'none',
      position: 'relative',
      overflow: 'hidden'
    },
    icon: {
      fontSize: '24px',
      marginBottom: '4px',
      filter: 'grayscale(0%)'
    },
    label: {
      fontSize: '10px',
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
      lineHeight: 1
    },
    ripple: {
      position: 'absolute',
      borderRadius: '50%',
      transform: 'scale(0)',
      animation: 'ripple 0.6s ease-out'
    }
  };

  const handleClick = (path) => {
    navigate(path);
  };

  const createRipple = (event, buttonRef) => {
    const button = buttonRef.current;
    if (!button) return;

    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    ripple.classList.add('ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <>
      <style>
        {`
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
          
          .floating-nav-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
          }
          
          .floating-nav-button:active {
            transform: translateY(0);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          
          .ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(0, 0, 0, 0.1);
            transform: scale(0);
            animation: ripple 0.6s ease-out;
          }
        `}
      </style>
      
      <div style={buttonStyles.container}>
        {buttons.map((button) => (
          <button
            key={button.id}
            ref={buttonRefs.current[button.id]}
            className="floating-nav-button"
            style={{
              ...buttonStyles.button,
              borderTop: `3px solid ${button.color}`
            }}
            onClick={(e) => {
              createRipple(e, buttonRefs.current[button.id]);
              handleClick(button.path);
            }}
            aria-label={`Navigate to ${button.label}`}
          >
            <span style={buttonStyles.icon} role="img" aria-label={button.label}>
              {button.icon}
            </span>
            <span style={buttonStyles.label}>
              {button.label}
            </span>
          </button>
        ))}
      </div>
    </>
  );
};

export default FloatingNavButtons;